import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, Input, Select, Spin, Collapse, Modal, message } from "antd";
import StandardButton from '../components/StandardButton';
import { toast } from "react-hot-toast";
import api from "../interceptors/api";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

const { TextArea } = Input;
const { Option } = Select;

const technologies = [
  "NodeJS", "JavaScript", "TypeScript", "Python", "Java", "C#", "C++", "Go", "Rust", "Ruby", "React Native", "PHP", "Swift", "Kotlin", "SQL", "Shell Script", "HTML", "CSS"
];

const CodeReview = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [technology, setTechnology] = useState("NodeJS");
  const [code, setCode] = useState(`function processData(data) {
  let result = [];
  for (let i = 0; i < data.length; i++) {
    if(data[i] !== null && data[i] !== undefined) {
      result.push(data[i] * 2 + 7); // Magic numbers
    }
  }
  // Unused variable
  let temp = 5;
  return result;
}
console.log(processData([1, 2, null, 3, 4]));`);
  const [loading, setLoading] = useState(false);
  const [review, setReview] = useState("");
  const [history, setHistory] = useState(() => {
    const saved = localStorage.getItem("codeReviewHistory");
    return saved ? JSON.parse(saved) : [];
  });

  // Load shared data if share parameter exists
  useEffect(() => {
    const shareId = searchParams.get("share");
    if (shareId) {
      loadSharedData(shareId);
    }
  }, []);

  const loadSharedData = (shareId) => {
    api.get(`/code-review/share/${shareId}`)
      .then(res => {
        setTechnology(res.data.technology);
        setCode(res.data.code);
        setReview(res.data.review);
        toast.success(t("shared_configuration_loaded"));
      })
      .catch(() => toast.error(t("invalid_share_link")));
  };

  const handleReview = async () => {
    if (!code.trim()) return toast.error(t("review_error_no_code"));
    const toastId = toast.loading(t("review_loading"));
    setLoading(true);
    setReview("");
    
    try {
      // Get user data from localStorage
      const userProfileData = JSON.parse(localStorage.getItem('userProfileData') || 'null');

      if (!userProfileData?.id) {
        toast.error('Please log in to use this feature');
        setLoading(false);
        return;
      }

      const res = await api.post("/code-review", {
        code,
        technology
      });
      
      setReview(res.data.review);
      const newEntry = {
        technology,
        review: res.data.review,
        timestamp: new Date().toISOString()
      };
      const updatedHistory = [newEntry, ...history];
      setHistory(updatedHistory);
      localStorage.setItem("codeReviewHistory", JSON.stringify(updatedHistory));
      toast.success(t("review_complete"), { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.error || t("review_error_api"), { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!review) return;
    try {
      await navigator.clipboard.writeText(review);
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const handleShare = async () => {
    if (!code.trim() || !review) return toast.error(t("share_error_no_data"));
    try {
      const res = await api.post("/code-review/share", {
        technology,
        code,
        review,
      });
      if (res.data?.id) {
        const url = `${window.location.origin}/tools/code-reviewer?share=${res.data.id}`;
        await navigator.clipboard.writeText(url);
        toast.success(t("share_success"));
      } else {
        toast.error(t("share_error"));
      }
    } catch (err) {
      toast.error(err.response?.data?.error || t("share_error"));
    }
  };

  return (
    <ProtectedToolWrapper showShareBanner={searchParams.has("share")}>
      <div className="max-w-5xl mx-auto">
        <Card className="p-6 rounded-xl shadow-lg space-y-6">
          <div className="mb-4 flex gap-2 flex-wrap">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">{t("language_label")}</label>
              <Select value={technology} onChange={setTechnology} className="w-40">
                {technologies.map(tg => <Option key={tg} value={tg}>{tg}</Option>)}
              </Select>
            </div>
          </div>
          <TextArea
            rows={8}
            value={code}
            onChange={e => setCode(e.target.value)}
            placeholder={t("code_input_placeholder")}
            className="mb-4"
          />
          <div className="mt-2 mb-4">
            <StandardButton 
              type="secondary" 
              onClick={handleReview} 
              disabled={loading}
              loading={loading}
            >
              {t("review_button")}
            </StandardButton>
          </div>

          {review && (
            <div className="mt-6">
              <h3 className="text-2xl font-bold text-black-700 mb-4">{t("review_label")}</h3>
              <div className="prose max-w-none">
                <ReactMarkdown
                  components={{
                    code({ node, inline, className, children, ...props }) {
                      const match = /language-(\w+)/.exec(className || "");
                      return !inline && match ? (
                        <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
                          {String(children).replace(/\n$/, "")}
                        </SyntaxHighlighter>
                      ) : (
                        <code {...props}>{children}</code>
                      );
                    }
                  }}
                >
                  {review}
                </ReactMarkdown>
              </div>
              <div className="flex gap-4 mt-4">
                <StandardButton type="secondary" size="small" onClick={handleCopy}>{t("copy")}</StandardButton>
                <StandardButton type="secondary" size="small" onClick={handleShare}>{t("share")}</StandardButton>
              </div>
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-10">
              <h3 className="text-lg font-semibold mb-4">{t("history")}</h3>
              <Collapse accordion>
                {history.map((h, i) => (
                  <Collapse.Panel
                    key={i}
                    header={<div><div className="text-xs text-gray-500">{h.technology} | {new Date(h.timestamp).toLocaleString()}</div>
                    </div>}>
                    <div className="prose max-w-none">
                      <ReactMarkdown
                        components={{
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || "");
                            return !inline && match ? (
                              <SyntaxHighlighter style={oneDark} language={match[1]} PreTag="div" {...props}>
                                {String(children).replace(/\n$/, "")}
                              </SyntaxHighlighter>
                            ) : (
                              <code {...props}>{children}</code>
                            );
                          }
                        }}
                      >
                        {h.review}
                      </ReactMarkdown>
                    </div>
                  </Collapse.Panel>
                ))}
              </Collapse>
              <StandardButton 
                type="secondary" 
                danger
                onClick={() => { localStorage.removeItem("codeReviewHistory"); setHistory([]); }} 
                className="mt-4"
              >
                {t("clear_history_button")}
              </StandardButton>
            </div>
          )}
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
};

export default CodeReview;